/* ==========================================================================
   FILE: global/components/Select.jsx
   IAMONEAI - Select/Dropdown Component
   Custom select with search, multi-select, and styling options
   ========================================================================== */

import React, { forwardRef, useState, useRef, useEffect, useId } from 'react';
import './Select.css';

// ==========================================================================
// NATIVE SELECT COMPONENT
// ==========================================================================

/**
 * Native select component (uses browser default dropdown)
 * Best for accessibility and mobile devices
 * 
 * @param {Object} props
 * @param {Array} options - Array of { value, label, disabled? }
 * @param {string} value - Selected value
 * @param {Function} onChange - Change handler
 * @param {string} label - Select label
 * @param {string} placeholder - Placeholder text
 * @param {string} error - Error message
 * @param {'sm'|'md'|'lg'} size - Select size
 * 
 * @example
 * <Select
 *   label="Country"
 *   options={[
 *     { value: 'us', label: 'United States' },
 *     { value: 'uk', label: 'United Kingdom' },
 *   ]}
 *   value={country}
 *   onChange={(e) => setCountry(e.target.value)}
 * />
 */
const Select = forwardRef(({
  options = [],
  value,
  onChange,
  label,
  placeholder = 'Select an option',
  error,
  hint,
  size = 'md',
  disabled = false,
  required = false,
  fullWidth = true,
  className = '',
  id: providedId,
  name,
  ...props
}, ref) => {
  const generatedId = useId();
  const id = providedId || generatedId;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const hasError = Boolean(error);

  const wrapperClasses = [
    'select-wrapper',
    fullWidth && 'select-full-width',
    className,
  ].filter(Boolean).join(' ');

  const selectClasses = [
    'select',
    `select-${size}`,
    hasError && 'select-error',
    disabled && 'select-disabled',
    !value && 'select-placeholder',
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {/* Label */}
      {label && (
        <label htmlFor={id} className="select-label">
          {label}
          {required && <span className="select-required" aria-hidden="true">*</span>}
        </label>
      )}

      {/* Select Container */}
      <div className="select-container">
        <select
          ref={ref}
          id={id}
          name={name}
          className={selectClasses}
          value={value}
          onChange={onChange}
          disabled={disabled}
          required={required}
          aria-invalid={hasError}
          aria-describedby={
            [hasError && errorId, hint && hintId].filter(Boolean).join(' ') || undefined
          }
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>

        {/* Chevron Icon */}
        <span className="select-icon" aria-hidden="true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </div>

      {/* Error Message */}
      {hasError && (
        <p id={errorId} className="select-error-message" role="alert">
          {error}
        </p>
      )}

      {/* Hint Text */}
      {hint && !hasError && (
        <p id={hintId} className="select-hint">
          {hint}
        </p>
      )}
    </div>
  );
});

Select.displayName = 'Select';

// ==========================================================================
// CUSTOM DROPDOWN SELECT COMPONENT
// ==========================================================================

/**
 * Custom styled dropdown select with search
 * 
 * @example
 * <CustomSelect
 *   label="Choose a color"
 *   options={colors}
 *   value={selectedColor}
 *   onChange={setSelectedColor}
 *   searchable
 * />
 */
export function CustomSelect({
  options = [],
  value,
  onChange,
  label,
  placeholder = 'Select an option',
  error,
  hint,
  size = 'md',
  disabled = false,
  required = false,
  fullWidth = true,
  searchable = false,
  clearable = false,
  className = '',
  id: providedId,
}) {
  const generatedId = useId();
  const id = providedId || generatedId;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const hasError = Boolean(error);
  const selectedOption = options.find(opt => opt.value === value);

  // Filter options based on search
  const filteredOptions = searchable && searchTerm
    ? options.filter(opt =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when opening
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        if (!isOpen) {
          event.preventDefault();
          setIsOpen(true);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        }
        break;
      default:
        break;
    }
  };

  const handleSelect = (optionValue) => {
    onChange?.(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange?.(null);
  };

  const wrapperClasses = [
    'custom-select-wrapper',
    fullWidth && 'select-full-width',
    className,
  ].filter(Boolean).join(' ');

  const triggerClasses = [
    'custom-select-trigger',
    `select-${size}`,
    isOpen && 'custom-select-open',
    hasError && 'select-error',
    disabled && 'select-disabled',
    !value && 'select-placeholder',
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses} ref={containerRef}>
      {/* Label */}
      {label && (
        <label htmlFor={id} className="select-label">
          {label}
          {required && <span className="select-required" aria-hidden="true">*</span>}
        </label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        id={id}
        className={triggerClasses}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-invalid={hasError}
      >
        <span className="custom-select-value">
          {selectedOption ? selectedOption.label : placeholder}
        </span>

        <span className="custom-select-icons">
          {/* Clear Button */}
          {clearable && value && (
            <span
              className="custom-select-clear"
              onClick={handleClear}
              role="button"
              aria-label="Clear selection"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </span>
          )}

          {/* Chevron */}
          <span className={`custom-select-chevron ${isOpen ? 'custom-select-chevron-open' : ''}`}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </span>
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="custom-select-dropdown" role="listbox">
          {/* Search Input */}
          {searchable && (
            <div className="custom-select-search">
              <svg
                className="custom-select-search-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                className="custom-select-search-input"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {/* Options */}
          <div className="custom-select-options">
            {filteredOptions.length === 0 ? (
              <div className="custom-select-empty">No options found</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={[
                    'custom-select-option',
                    option.value === value && 'custom-select-option-selected',
                    option.disabled && 'custom-select-option-disabled',
                  ].filter(Boolean).join(' ')}
                  onClick={() => !option.disabled && handleSelect(option.value)}
                  disabled={option.disabled}
                  role="option"
                  aria-selected={option.value === value}
                >
                  {option.icon && (
                    <span className="custom-select-option-icon">{option.icon}</span>
                  )}
                  <span className="custom-select-option-label">{option.label}</span>
                  {option.value === value && (
                    <svg
                      className="custom-select-option-check"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {hasError && (
        <p className="select-error-message" role="alert">
          {error}
        </p>
      )}

      {/* Hint Text */}
      {hint && !hasError && (
        <p className="select-hint">
          {hint}
        </p>
      )}
    </div>
  );
}

// ==========================================================================
// CONTEXT SELECT COMPONENT (for IAMONEAI context switching)
// ==========================================================================

/**
 * Special select for switching between memory contexts
 * 
 * @example
 * <ContextSelect
 *   value={activeContext}
 *   onChange={setActiveContext}
 * />
 */
export function ContextSelect({
  value,
  onChange,
  contexts = [
    { value: 'personal', label: 'Personal', icon: '🏠', color: 'var(--context-personal)' },
    { value: 'work', label: 'Work', icon: '💼', color: 'var(--context-work)' },
    { value: 'family', label: 'Family', icon: '👨‍👩‍👧‍👦', color: 'var(--context-family)' },
  ],
  disabled = false,
  size = 'md',
  className = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedContext = contexts.find(ctx => ctx.value === value) || contexts[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (contextValue) => {
    onChange?.(contextValue);
    setIsOpen(false);
  };

  const triggerClasses = [
    'context-select-trigger',
    `context-select-${size}`,
    isOpen && 'context-select-open',
    disabled && 'context-select-disabled',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className="context-select-wrapper" ref={containerRef}>
      {/* Trigger */}
      <button
        type="button"
        className={triggerClasses}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{ '--context-color': selectedContext.color }}
      >
        <span className="context-select-icon">{selectedContext.icon}</span>
        <span className="context-select-label">{selectedContext.label}</span>
        <svg
          className={`context-select-chevron ${isOpen ? 'context-select-chevron-open' : ''}`}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="context-select-dropdown">
          {contexts.map((context) => (
            <button
              key={context.value}
              type="button"
              className={[
                'context-select-option',
                context.value === value && 'context-select-option-selected',
              ].filter(Boolean).join(' ')}
              onClick={() => handleSelect(context.value)}
              style={{ '--context-color': context.color }}
            >
              <span className="context-select-option-icon">{context.icon}</span>
              <span className="context-select-option-label">{context.label}</span>
              {context.value === value && (
                <svg
                  className="context-select-option-check"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default Select;